import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { SchemaValidationFailedError } from '@kilat/shared';

import { functions } from '../firebase.js';

/**
 * Typed wrapper around the `exportReport` Cloud Function (gap G9).
 *
 * The export runs server-side because the dashboard reads daily stats through
 * a BOUNDED listener — a client-side export could only ever hand back the rows
 * already on screen, so an operator asking for a year got a truncated file with
 * nothing saying so.
 */

export const ExportReportInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  /** Inclusive SGT day keys, `YYYY-MM-DD`. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type ExportReportInput = z.infer<typeof ExportReportInputSchema>;

export const ExportReportOutputSchema = z.object({
  filename: z.string().min(1),
  contentType: z.literal('text/csv'),
  csv: z.string(),
  rowCount: z.number().int().nonnegative(),
});
export type ExportReportOutput = z.infer<typeof ExportReportOutputSchema>;

const exportReportFn: HttpsCallable<ExportReportInput, ExportReportOutput> = httpsCallable(
  functions,
  'exportReport',
);

export async function callExportReport(input: ExportReportInput): Promise<ExportReportOutput> {
  const parsed = ExportReportInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('exportReport.input', parsed.error.flatten());
  }
  const result = await exportReportFn(parsed.data);
  const out = ExportReportOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('exportReport.output', out.error.flatten());
  }
  return out.data;
}
